import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

const MODULES = ["GENERAL_LEDGER", "PROCUREMENT", "INVENTORY", "REVENUE", "ASSETS_PLANT", "PRODUCTION", "PROJECT_DPR", "LEGAL", "REPORTS", "ADMINISTRATION"];
const ACTIONS = ["VIEW", "CREATE", "APPROVE", "POST", "DELETE"];
const ROLES = ["PORTFOLIO_LEAD", "MEMBER", "ACCOUNTS", "PROCUREMENT", "STORES", "HR"];

export default function PermissionMatrix() {
  const [module, setModule] = useState(MODULES[0]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/permission-matrix");
      setEntries(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const valueFor = (role, action) => entries.find((e) => e.role === role && e.module === module && e.action === action)?.allowed ?? true;

  const toggle = async (role, action, allowed) => {
    const key = `${role}-${action}`;
    setSaving(key);
    setEntries((prev) => prev.map((e) => (e.role === role && e.module === module && e.action === action ? { ...e, allowed } : e)));
    try {
      await api.put("/admin/permission-matrix", { entries: [{ role, module, action, allowed }] });
    } catch (err) {
      toast.error(getErrorMessage(err));
      load();
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="permission-matrix-page">
      <PageHeader
        title="Permission Matrix"
        description="Module x action per role. ORG_ADMIN always has full access and is not shown here."
        action={
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger className="w-56" data-testid="permission-matrix-module-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MODULES.map((m) => <SelectItem key={m} value={m}>{m.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Role</TableHead>{ACTIONS.map((a) => <TableHead key={a} className="text-center">{a}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {ROLES.map((role) => (
                <TableRow key={role} data-testid={`permission-matrix-row-${role}`}>
                  <TableCell className="font-medium text-foreground">{role}</TableCell>
                  {ACTIONS.map((action) => (
                    <TableCell key={action} className="text-center">
                      {saving === `${role}-${action}` ? (
                        <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Switch checked={valueFor(role, action)} onCheckedChange={(v) => toggle(role, action, v)} data-testid={`permission-toggle-${role}-${action}`} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
